/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useEffect, Fragment } from 'react';
import { IntlProvider } from 'react-intl';
import { ReactQueryDevtools } from 'react-query-devtools';
import { ThemeSwitcherProvider } from 'react-css-theme-switcher';
import moment from 'moment';
import 'moment/locale/da';
import 'moment/locale/sv';

// langualge setup for Ant Designs components
import { ConfigProvider } from 'antd';
import daDK from 'antd/es/locale/da_DK';
import svSE from 'antd/es/locale/sv_SE';

// language support setup
import { getIntlMessages } from '~src/i18n';
import { LANGUAGE } from '~src/config';
import { APP_VERSION } from '~src/config/app-version';
// local imports
import Root from '~components/pages/Root';
import Icons from './icons/Icons';
import { appLog } from './utils/log';

// manages the themes needed for Ant Design
const themes = {
  light: `${process.env.PUBLIC_URL}/light-theme.css`,
  dark: `${process.env.PUBLIC_URL}/dark-theme.css`,
  su: `${process.env.PUBLIC_URL}/su-theme.css`,
};

// ant locale, for now supports "da", "sv" or "en"
const antLocale = (() => {
  switch (true) {
    case LANGUAGE.startsWith('da'):
      return daDK;
    case LANGUAGE.startsWith('sv'):
      return svSE;
    default:
      return undefined;
  }
})();

/** App starting point, includes all configuration & global providers;
 * renders navigation <Root />
 */
const App = () => {
  // mount: moment locale setup
  useEffect(() => {
    document.documentElement.lang = LANGUAGE;

    // MOMENT DATE'S LANGUAGE SETUP
    moment.locale(LANGUAGE);

    if (LANGUAGE.startsWith('da') || LANGUAGE.startsWith('sv')) {
      const currentLocale = moment.locale();
      const localeData = moment.localeData(currentLocale);

      moment.updateLocale(currentLocale, {
        // MONTHS: CAPITALIZE FULL AND SHORT NAMES
        months: localeData
          .months()
          .map((m) => m.charAt(0).toUpperCase() + m.slice(1)),
        monthsShort: localeData
          .monthsShort()
          .map((m) => m.charAt(0).toUpperCase() + m.slice(1)),

        // WEEKDAYS: CAPITALIZE FULL, SHORT AND MINIMAL NAMES
        weekdays: localeData
          .weekdays()
          .map((d) => d.charAt(0).toUpperCase() + d.slice(1)),
        weekdaysShort: localeData
          .weekdaysShort()
          .map((d) => d.charAt(0).toUpperCase() + d.slice(1)),
        weekdaysMin: localeData
          .weekdaysMin()
          .map((d) => d.charAt(0).toUpperCase() + d.slice(1)),
      });
    }

    appLog('Language:', LANGUAGE);
  }, []);

  return (
    <Fragment>
      <ConfigProvider locale={antLocale}>
        <ThemeSwitcherProvider themeMap={themes} defaultTheme="light">
          <IntlProvider
            textComponent={Fragment}
            locale={LANGUAGE}
            messages={getIntlMessages(LANGUAGE)}>
            <div className="App">
              <Root />
            </div>
          </IntlProvider>
        </ThemeSwitcherProvider>
      </ConfigProvider>

      {/* import of the icons that are used thoughout the project */}
      <Icons />
      <ReactQueryDevtools />
    </Fragment>
  );
};

export default App;
